import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Card, Space, Modal, Progress, message, Tooltip } from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import AddSpecialities from './Components/AddSpecialities';
import axios from 'axios';
import { oneKlicks } from '../SDK/oneclick';
import { OneKlicksFileSDK } from '../SDK/OneKlicksFileSDK';
import ImageShow from '../Uitiles/ImageShow';
import { getuserdata } from '../services/apiService';
import EditSpecialities from './Components/EditSpecialities';
import { debugLog } from '../utils/debug';

// Speciality Model
class SpecialityModel {
    constructor(data = {}) {
        this.id = data.id || '';
        this.old_id = data.old_id || '';
        this.name = data.name || '';
        this.slug = data.slug || '';
        this.color = data.color || '';
        this.url = data.url || '';
        this.totalUse = data.totalUse || 0;
        this.status = data.status || 'publish';
        this.country = data.country || '';
        this.created_at = data.created_at || new Date().toISOString();
        this.updated_at = data.updated_at || new Date().toISOString();
    }

    toJSON() {
        return {
            id: this.id,
            old_id: this.old_id,
            name: this.name,
            slug: this.slug,
            color: this.color,
            url: this.url,
            totalUse: this.totalUse,
            status: this.status,
            country: this.country,
            created_at: this.created_at,
            updated_at: this.updated_at
        };
    }

    static fromAPI(data) {
        return new SpecialityModel({
            id: data.id,
            old_id: data.old_id,
            name: data.name,
            slug: data.slug,
            color: data.color,
            url: data.url,
            totalUse: data.totalUse,
            status: data.status,
            country: data.country,
            created_at: data.created_at,
            updated_at: data.updated_at
        });
    }
}

const Specialities = () => {
    const [searchText, setSearchText] = useState('');
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [modalData, setModalData] = useState(new SpecialityModel());
    const [imagePreview, setImagePreview] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [currentImportItem, setCurrentImportItem] = useState('');
    const [usertype, setUsertype] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState({});
    const [showFixModal, setShowFixModal] = useState(false);
    const [isFixing, setIsFixing] = useState(false);
    const [openAiKey, setOpenAiKey] = useState(process.env.REACT_APP_OPENAI_API_KEY || '');

    useEffect(() => {
        getSpecialities();
    }, []);

    useEffect(() => {
        getSpecialities();
    }, [searchText]);

    const getSpecialities = async () => {
        const tableSchema = '';
        const tableName = 'specialities';
        const conditions = {};
        if (searchText) {
            conditions.name = searchText;
        }
        const orderBy = '';
        const search = '';
        const pagination = '';
        const margedata = '';
        const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
        debugLog('getSpecialities response', response);
        const specialities = response.data.map(item => SpecialityModel.fromAPI(item));
        setData(specialities);
        const usertype = await getuserdata('user_type');
        setUsertype(usertype);
    }

    const decodeHtml = (html) => {
        const txt = document.createElement('textarea');
        txt.innerHTML = html;
        return txt.value;
    };

    const getPromptForSpeciality = (speciality) => {
        const backgroundColors = ['deep navy blue', 'soft pastel blue', 'mint green', 'light coral', 'lavender', 'pale yellow', 'peach', 'light cyan', 'warm dark gray', 'rose pink', 'light teal', 'sky blue', 'periwinkle', 'forest green', 'deep plum'];

        // Generate a deterministic color based on the speciality name and ID
        // This ensures different specialities get different colors, but the same speciality always gets the same color.
        const hashStr = (speciality.id || '') + '_' + (speciality.name || '');
        let hash = 0;
        for (let i = 0; i < hashStr.length; i++) {
            hash = hashStr.charCodeAt(i) + ((hash << 5) - hash);
        }
        const bgIndex = Math.abs(hash) % backgroundColors.length;
        const bgColor = backgroundColors[bgIndex];

        return `A beautiful, ultra-high quality, minimalist 3D emblem representing the medical specialty: ${speciality.name}. The emblem should feature a natural, highly realistic 3D medical object with authentic textures and a soft, warm, and professional color palette, centered on a clean circular base. The emblem must maintain a consistent size, taking up exactly 70% of the canvas frame with equal, ample padding on all sides. The total image size must be 1080x1080 pixels. The entire background of the image MUST be a completely solid, uniform ${bgColor} color. Professional healthcare design, natural soft lighting, clean, modern, photorealistic 3D render, similar to a premium app icon.`;
    };

    const handleCopyPrompt = (record) => {
        const prompt = getPromptForSpeciality(record);
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(prompt)
                .then(() => message.success(`Copied AI prompt for ${record.name} to clipboard!`))
                .catch(() => message.error('Failed to copy to clipboard.'));
        } else {
            // Fallback for non-secure contexts (e.g. HTTP IP address)
            const textArea = document.createElement("textarea");
            textArea.value = prompt;
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            textArea.style.top = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                message.success(`Copied AI prompt for ${record.name} to clipboard!`);
            } catch (error) {
                console.error("Failed to copy text", error);
                message.error("Failed to copy prompt to clipboard");
            }
            textArea.remove();
        }
    };

    const handleUpdateSingleImage = async (record) => {
        if (!openAiKey || !openAiKey.startsWith('sk-')) {
            setShowFixModal(true);
            message.info('Please enter your OpenAI API key to continue.');
            return;
        }

        const hide = message.loading(`Generating new 3D image for ${record.name}...`, 0);

        try {
            const prompt = getPromptForSpeciality(record);
            const response = await axios.post('https://api.openai.com/v1/images/generations', {
                model: "gpt-image-2",
                prompt: prompt,
                n: 1,
                size: "1080x1080"
            }, {
                headers: {
                    'Authorization': `Bearer ${openAiKey}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && response.data.data && response.data.data.length > 0) {
                const imgData = response.data.data[0];
                let uploadimage = null;

                if (imgData.b64_json) {
                    const res = await fetch(`data:image/png;base64,${imgData.b64_json}`);
                    const blob = await res.blob();
                    const file = new File([blob], `${record.slug || 'speciality'}-${Date.now()}.png`, { type: 'image/png' });
                    uploadimage = await OneKlicksFileSDK.uploadFile('specialities', 'public', file);
                } else if (imgData.url) {
                    uploadimage = await OneKlicksFileSDK.uploadFileFromUrl('specialities', 'public', imgData.url);
                }

                if (uploadimage && (uploadimage.fileid || uploadimage.fileId)) {
                    const newUrl = uploadimage.fileid || uploadimage.fileId;
                    await oneKlicks.update('', 'specialities', { url: newUrl }, { id: record.id });
                    message.success(`Successfully generated and saved new image for ${record.name}!`);
                    getSpecialities();
                } else {
                    message.error(`Failed to save image for ${record.name}.`);
                }
            } else {
                message.error('Invalid response from OpenAI API.');
            }
        } catch (err) {
            console.error(`Failed to generate single image for ${record.name}`, err);
            const errorMsg = err.response?.data?.error?.message || err.message;
            if (err.response && err.response.status === 401) {
                message.error('Invalid OpenAI API Key.');
                setShowFixModal(true);
            } else {
                message.error(`OpenAI Error: ${errorMsg}`);
            }
        } finally {
            hide();
        }
    };

    const handleRemoveDuplicates = async () => {
        const nameMap = {};
        const duplicatesToRemove = [];

        data.forEach(item => {
            const normalizedName = decodeHtml(item.name).trim().toLowerCase();
            if (nameMap[normalizedName]) {
                // Keep the existing one, mark this one for deletion
                duplicatesToRemove.push(item);
            } else {
                nameMap[normalizedName] = item;
            }
        });

        if (duplicatesToRemove.length === 0) {
            message.info('No duplicate specialities found!');
            return;
        }

        const confirm = window.confirm(`Found ${duplicatesToRemove.length} duplicate specialities with the same name. Are you sure you want to permanently delete the duplicates?`);
        if (!confirm) return;

        const hide = message.loading(`Deleting ${duplicatesToRemove.length} duplicates...`, 0);
        let deleteCount = 0;

        try {
            for (const dup of duplicatesToRemove) {
                await oneKlicks.delete('', 'specialities', { id: dup.id });
                if (dup.url && typeof dup.url === 'string' && dup.url.length > 5) {
                    try {
                        await OneKlicksFileSDK.deleteFile(dup.url);
                    } catch (e) {
                        // ignore file deletion errors if file already missing
                    }
                }
                deleteCount++;
            }
            message.success(`Successfully removed ${deleteCount} duplicate specialities.`);
            getSpecialities();
        } catch (error) {
            console.error('Error removing duplicates:', error);
            message.error('Failed to remove some duplicates.');
        } finally {
            hide();
        }
    };

    const columns = [
        {
            title: 'ID',
            key: 'id',
            width: '10%',
            align: 'center',
            render: (text, record, index) => index + 1,
            sorter: false // Optional: disable sorting since this is just an index
        },
        {
            title: 'Image',
            dataIndex: 'url',
            key: 'url',
            width: '15%',
            align: 'center',
            render: (text) => (
                <ImageShow image={text} width={40} height={40} style={{ objectFit: 'cover', borderRadius: '50%' }} />
            )
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => decodeHtml(a.name).localeCompare(decodeHtml(b.name)),
            filteredValue: searchText ? [searchText] : null,
            onFilter: (value, record) =>
                decodeHtml(record.name).toLowerCase().includes(value.toLowerCase()),
            width: '40%',
            ellipsis: true,
            render: (text) => decodeHtml(text)
        },
        {
            title: 'Total Use',
            dataIndex: 'totalUse',
            key: 'totalUse',
            sorter: (a, b) => a.totalUse - b.totalUse,
            width: '20%',
            align: 'center'
        },
        {
            title: 'Country',
            dataIndex: 'country',
            key: 'country',
            width: '15%',
            align: 'center',
            render: (country) => (
                <p className="text-capitalize">{country}</p>
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: '20%',
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit Speciality">
                        <Button type="primary" ghost icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
                    </Tooltip>
                    <Tooltip title="Delete Speciality">
                        <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} size="small" />
                    </Tooltip>
                    {/* <Tooltip title="Auto-Update Image">
                        <Button style={{ color: '#17a2b8', borderColor: '#17a2b8' }} icon={<i className="ti-image" />} onClick={() => handleUpdateSingleImage(record)} size="small" />
                    </Tooltip> */}
                    <Tooltip title="Copy Prompt">
                        <Button style={{ color: '#6c757d', borderColor: '#6c757d' }} icon={<i className="ti-files" />} onClick={() => handleCopyPrompt(record)} size="small" />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const openModal = () => {
        setShowModal(true);
        setModalData(new SpecialityModel());
        setImagePreview(null);
    };

    const closeModal = () => setShowModal(false);

    const openImportModal = () => {
        setShowImportModal(true);
    };

    const closeImportModal = () => {
        setShowImportModal(false);
    };

    const handleImport = async () => {
        setIsImporting(true);
        setImportProgress(0);

        try {
            const response = await axios.post('//css.meddylist.com//wp-json/api/v1/taxonomies/get-specilities');
            const specialities = response.data;

            if (!Array.isArray(specialities)) {
                throw new Error('Invalid data format received');
            }

            const totalItems = specialities.length;
            let processedItems = 0;
            let successCount = 0;
            let errorCount = 0;

            for (const speciality of specialities) {
                setCurrentImportItem(speciality.name || 'Processing item...');

                try {
                    const root = 'specialities';
                    const status = 'publish';
                    const url = speciality.url;
                    const uploadimage = await OneKlicksFileSDK.uploadFileFromUrl(root, status, url);
                    console.log(uploadimage);
                    const tableSchema = '';
                    const tableName = 'specialities';

                    const specialityModel = new SpecialityModel({
                        old_id: speciality.id,
                        name: speciality.name,
                        slug: speciality.slug,
                        color: speciality.color || '',
                        url: uploadimage.fileId,
                        status: 'publish'
                    });

                    const response = await oneKlicks.create(tableSchema, tableName, specialityModel.toJSON(), {});

                    if (response && response.success) {
                        successCount++;
                        message.success(`Successfully imported: ${speciality.name}`);
                    } else {
                        errorCount++;
                        message.error(`Failed to import: ${speciality.name}`);
                    }

                    processedItems++;
                    const progress = Math.round((processedItems / totalItems) * 100);
                    setImportProgress(progress);

                    await new Promise(resolve => setTimeout(resolve, 500));
                } catch (error) {
                    errorCount++;
                    console.error(`Error processing speciality ${speciality.name}:`, error);
                    message.error(`Failed to import ${speciality.name}: ${error.message}`);
                }
            }

            message.success(`Import completed! Successfully imported ${successCount} items. ${errorCount} items failed.`);
            await getSpecialities(); // Refresh the list after import

        } catch (error) {
            console.error('Import failed:', error);
            message.error('Import failed. Please try again.');
        } finally {
            setIsImporting(false);
            setCurrentImportItem('');
            setImportProgress(0);
            closeImportModal();
        }
    };

    const handleSubmit = () => {
        setData([...data, modalData]);
        closeModal();
    };

    const handleEdit = (record) => {
        setModalData(record);
        setEditData(record);
        setShowEditModal(true);
    };

    const handleDelete = (record) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete "${decodeHtml(record.name)}"?`);

        if (confirmDelete) {
            try {
                const tableSchema = '';
                const tableName = 'specialities';
                oneKlicks.delete(tableSchema, tableName, { id: record.id })
                    .then(response => {
                        if (response && response.success) {
                            message.success('Speciality deleted successfully');
                            OneKlicksFileSDK.deleteFile(record.url).then(response => {
                                console.log(response);
                                getSpecialities();
                            })
                        } else {
                            message.error('Failed to delete speciality');
                        }
                    })
                    .catch(error => {
                        console.error('Error deleting speciality:', error);
                        message.error('Failed to delete speciality');
                    });

            } catch (error) {
                console.error('Error deleting speciality:', error);
                message.error('Failed to delete speciality');
            }
        }
    };

    const openFixModal = () => setShowFixModal(true);
    const closeFixModal = () => setShowFixModal(false);

    const autoUpdateAllImages = async () => {
        setIsFixing(true);
        setImportProgress(0);
        let successCount = 0;
        const totalItems = data.length;

        if (totalItems === 0) {
            message.info('No specialities available to update.');
            setIsFixing(false);
            closeFixModal();
            return;
        }

        if (!openAiKey || !openAiKey.startsWith('sk-')) {
            message.error('Please enter a valid OpenAI API Key to use the DALL-E generator.');
            setIsFixing(false);
            return;
        }

        for (let i = 0; i < totalItems; i++) {
            const speciality = data[i];
            setCurrentImportItem(`Generating DALL-E image for: ${speciality.name}`);

            try {
                // Call OpenAI API for high-quality, professional generation using the specific model allowed by this key
                const prompt = getPromptForSpeciality(speciality);

                const response = await axios.post('https://api.openai.com/v1/images/generations', {
                    model: "gpt-image-2",
                    prompt: prompt,
                    n: 1,
                    size: "1080x1080"
                }, {
                    headers: {
                        'Authorization': `Bearer ${openAiKey}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data && response.data.data && response.data.data.length > 0) {
                    const imgData = response.data.data[0];
                    let uploadimage = null;

                    if (imgData.b64_json) {
                        // The model returned base64 instead of a URL. Convert to a File object.
                        const res = await fetch(`data:image/png;base64,${imgData.b64_json}`);
                        const blob = await res.blob();
                        const file = new File([blob], `${speciality.slug || 'speciality'}-${Date.now()}.png`, { type: 'image/png' });
                        uploadimage = await OneKlicksFileSDK.uploadFile('specialities', 'public', file);
                    } else if (imgData.url) {
                        // Model returned a standard URL
                        uploadimage = await OneKlicksFileSDK.uploadFileFromUrl('specialities', 'public', imgData.url);
                    }

                    if (uploadimage && (uploadimage.fileid || uploadimage.fileId)) {
                        const newUrl = uploadimage.fileid || uploadimage.fileId;
                        await oneKlicks.update('', 'specialities', { url: newUrl }, { id: speciality.id });
                        successCount++;
                    } else {
                        console.error('Failed to upload image via SDK:', uploadimage);
                    }
                } else {
                    console.error('Invalid response from OpenAI:', response.data);
                }
            } catch (err) {
                console.error(`Failed to fix image for ${speciality.name}`, err);
                const errorMsg = err.response?.data?.error?.message || err.message;

                if (err.response && err.response.status === 401) {
                    message.error('Invalid OpenAI API Key. Stopping automation.');
                    setIsFixing(false);
                    return;
                } else if (err.response && err.response.status === 400) {
                    message.error(`OpenAI Error: ${errorMsg}`);
                    setIsFixing(false);
                    return; // Stop the loop so we don't spam 400 errors
                } else {
                    message.error(`Failed to generate image: ${errorMsg}`);
                }
            }

            setImportProgress(Math.round(((i + 1) / totalItems) * 100));
        }

        setIsFixing(false);
        setCurrentImportItem('');
        message.success(`Successfully updated ${successCount} images with high quality replacements.`);
        getSpecialities();
        closeFixModal();
    };

    return (
        <section className="dc-haslayout dc-dbsectionspace" style={{ backgroundColor: '#f4f7fe', minHeight: '100vh', padding: '24px' }}>
            <div>
                <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
                    <Card
                        style={{
                            borderRadius: '16px',
                            border: 'none',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                            background: 'white'
                        }}
                        bodyStyle={{ padding: '24px' }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '16px',
                            marginBottom: '28px',
                            paddingBottom: '20px',
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontWeight: 700, color: '#2b3674', fontSize: '24px' }}>Specialities Management</h3>
                                <p style={{ margin: 0, color: '#a3aed1', fontSize: '14px', marginTop: '4px' }}>Manage, generate, and import all your medical categories</p>
                            </div>

                            <Space size="middle" wrap>
                                <Input
                                    placeholder="Search Speciality..."
                                    prefix={<SearchOutlined style={{ color: '#a3aed1', fontSize: '16px' }} />}
                                    style={{
                                        width: 280,
                                        borderRadius: '12px',
                                        backgroundColor: '#f4f7fe',
                                        border: 'none',
                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                                    }}
                                    size="large"
                                    onChange={(e) => setSearchText(e.target.value)}
                                    allowClear
                                />
                                <Button
                                    icon={<DeleteOutlined />}
                                    onClick={handleRemoveDuplicates}
                                    style={{ borderRadius: '10px', fontWeight: 600, background: '#fff1f0', color: '#f5222d', border: '1px solid #ffa39e', height: '40px' }}
                                >
                                    Remove Duplicates
                                </Button>
                                <Button
                                    icon={<i className="ti-image" />}
                                    onClick={openFixModal}
                                    style={{ borderRadius: '10px', fontWeight: 600, background: '#e6f7ff', color: '#1890ff', border: '1px solid #91d5ff', height: '40px' }}
                                >
                                    Auto-Update Images
                                </Button>
                                {usertype === 'administrator' && (
                                    <Button
                                        icon={<i className="ti-import" />}
                                        onClick={openImportModal}
                                        style={{ borderRadius: '10px', fontWeight: 600, background: '#f6ffed', color: '#52c41a', border: '1px solid #b7eb8f', height: '40px' }}
                                    >
                                        Import
                                    </Button>
                                )}
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={openModal}
                                    style={{
                                        borderRadius: '10px',
                                        fontWeight: 600,
                                        background: 'linear-gradient(135deg, #1890ff 0%, #0050b3 100%)',
                                        border: 'none',
                                        boxShadow: '0 4px 14px rgba(24, 144, 255, 0.4)',
                                        height: '40px'
                                    }}
                                >
                                    Add Speciality
                                </Button>
                            </Space>
                        </div>

                        <Table
                            columns={columns}
                            dataSource={data}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                position: ['bottomRight'],
                                style: {
                                    paddingTop: '20px',
                                    paddingBottom: '10px',
                                    margin: 0,
                                    borderTop: '1px solid #f0f0f0'
                                }
                            }}
                            style={{
                                marginTop: '10px'
                            }}
                            rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
                        />
                    </Card>
                </div>

                {showModal && (
                    <AddSpecialities closeModal={closeModal} getSpecialities={getSpecialities} />
                )}

                {showEditModal && (
                    <EditSpecialities closeModal={setShowEditModal} data={editData} getSpecialities={getSpecialities} />
                )}

                <Modal
                    title="Import Specialities"
                    open={showImportModal}
                    onCancel={closeImportModal}
                    footer={[
                        <Button key="cancel" onClick={closeImportModal} disabled={isImporting} shape="round">
                            Cancel
                        </Button>,
                        <Button
                            key="import"
                            type="primary"
                            onClick={handleImport}
                            loading={isImporting}
                            disabled={isImporting}
                            shape="round"
                        >
                            Start Import
                        </Button>
                    ]}
                >
                    {isImporting ? (
                        <div>
                            <p>Importing specialities...</p>
                            <Progress percent={importProgress} status="active" />
                            <p style={{ marginTop: '10px' }}>{currentImportItem}</p>
                            <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                                Please do not close this window while import is in progress
                            </p>
                        </div>
                    ) : (
                        <p>Are you sure you want to import specialities? This process may take a few minutes.</p>
                    )}
                </Modal>

                <Modal
                    title="Generate Premium AI Images (OpenAI DALL-E 3)"
                    open={showFixModal}
                    onCancel={closeFixModal}
                    footer={[
                        <Button key="cancel" onClick={closeFixModal} disabled={isFixing}>
                            Cancel
                        </Button>,
                        <Button
                            key="import"
                            type="primary"
                            onClick={autoUpdateAllImages}
                            loading={isFixing}
                            disabled={isFixing || !openAiKey}
                        >
                            Generate Images
                        </Button>
                    ]}
                >
                    {isFixing ? (
                        <div>
                            <p>Calling OpenAI DALL-E 3 to generate premium images...</p>
                            <Progress percent={importProgress} status="active" />
                            <p style={{ marginTop: '10px', fontWeight: 'bold' }}>{currentImportItem}</p>
                            <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                                This process updates every speciality. Please do not close this window while it is running.
                            </p>
                        </div>
                    ) : (
                        <div>
                            <p>This will use <strong>OpenAI's state-of-the-art DALL-E 3 model</strong> to generate ultra-high-quality, custom 3D medical icons for all {data.length} specialities.</p>
                            <div className="mb-3 mt-4">
                                <label className="form-label fw-bold">OpenAI API Key</label>
                                <Input.Password
                                    placeholder="sk-..."
                                    value={openAiKey}
                                    onChange={(e) => setOpenAiKey(e.target.value)}
                                    size="large"
                                />
                                <small className="text-muted d-block mt-2">
                                    Your key is not stored anywhere. It is only used for this one-time automation loop. DALL-E 3 costs approx $0.04 per image.
                                </small>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </section>
    );
};

export default Specialities;










